'use strict';

import { renderVidyardPlayer } from './vidyard';
import { renderYoutubePlayer } from './youtube';
import { renderWistiaPlayer } from './wistia';
import acceptedCookiePolicy from "../../components/onetrust/cookiePolicy";

const renderVideoPlayer = videoContainer => {
    return new Promise((resolve, reject) => {
        const provider = videoContainer?.dataset?.provider;
        const code = videoContainer?.dataset?.code;
        const parameters = videoContainer?.dataset?.parameters;

        switch (provider) {
            case 'vidyard':
                renderVidyardPlayer({
                    container: videoContainer,
                    code,
                    onReady: player => {
                        if (player) {
                            enableVidyardTracking();

                            const playerWrapper = {
                                innerPlayer: player,
                                play: function() {
                                    if (this.innerPlayer) {
                                        this.innerPlayer.play();
                                    }
                                },
                                pause: function() {
                                    this.pausedBy = 'script';
                                    if (this.innerPlayer) {
                                        this.innerPlayer.pause();
                                    }
                                },
                                pausedBy: null,
                                videoElement: player.placeholder
                            };

                            player.on('pause', () => {
                                if (playerWrapper.pausedBy !== 'script') {
                                    playerWrapper.pausedBy = 'user';
                                }
                            });

                            player.on('play', () => {
                                if (playerWrapper.pausedBy === 'script') {
                                    playerWrapper.pausedBy = null;
                                }
                            });

                            CoveoWebsite.videoApi.register(code, playerWrapper);

                            resolve(playerWrapper);
                        }
                    }
                });

                break;
            case 'youtube':
                renderYoutubePlayer({
                    container: videoContainer,
                    code,
                    parameters,
                    onReady: player => {
                        if (player) {
                            const playerWrapper = {
                                innerPlayer: player,
                                play: function() {
                                    if (this.innerPlayer) {
                                        this.innerPlayer.target.playVideo();
                                    }
                                },
                                pause: function() {
                                    this.pausedBy = 'script';
                                    if (this.innerPlayer) {
                                        this.innerPlayer.target.pauseVideo();
                                    }
                                },
                                pausedBy: null
                            };

                            CoveoWebsite.videoApi.register(code, playerWrapper);

                            resolve(playerWrapper);
                        }
                    }
                });

                break;
            case 'wistia':
                renderWistiaPlayer({
                    id: code,
                    onReady: player => {
                        if (player) {
                            player.bind('play', () => {
                                player.container.setAttribute('data-mode', 'player');
                            });

                            const playerWrapper = {
                                innerPlayer: player,
                                play: function() {
                                    if (this.innerPlayer) {
                                        this.innerPlayer.play();
                                    }
                                },
                                pause: function() {
                                    this.pausedBy = 'script';

                                    if (this.innerPlayer) {
                                        this.innerPlayer.pause();
                                    }
                                },
                                pausedBy: null,
                                get state() {
                                    return this.innerPlayer.state();
                                },
                                videoElement: player.container
                            };

                            CoveoWebsite.videoApi.register(code, playerWrapper);

                            resolve(playerWrapper);
                        }
                    }
                });

                break;
            case 'html':
                const playerWrapper = {
                    innerPlayer: videoContainer,
                    play: function() {
                        if (this.innerPlayer) {
                            this.innerPlayer.play();
                        }
                    },
                    pause: function() {
                        this.pausedBy = 'script';
                        if (this.innerPlayer) {
                            this.innerPlayer.pause();
                        }
                    },
                    pausedBy: null
                };

                CoveoWebsite.videoApi.register(code, playerWrapper);

                resolve();

                break;
        }
    });
};

const enableVidyardTracking = () => {
    acceptedCookiePolicy({
        ifTargetingCookies: () => {
            VidyardV4.api.GDPR.consent(true);
        },
        ifNoTargetingCookies: () => {
            VidyardV4.api.GDPR.consent(false);
        }
    });
};

const pauseAllWistiaVideos = (videoCode) => {
    if (window.Wistia && window.Wistia.api) {
        const allWistiaVideos = window.Wistia.api.all();

        allWistiaVideos.forEach(video => {
            if (video._hashedId && video._hashedId !== videoCode) {
                video.pause();
            }
        });

        window.dispatchEvent(new CustomEvent('pause-videos'));
    }
};

const playVideoPlayer = (video, options) => {
    const videoCode = video.dataset?.code;
    pauseAllWistiaVideos(videoCode);
    
    if (video.dataset.rendered !== 'true') {
        video.dataset.rendered = true;
        video.querySelector('.wistia_embed')?.setAttribute('data-mode', 'player');

        const videoFrontElement = video.querySelector('[am-video-player-front]');

        if (videoFrontElement) {
            videoFrontElement.classList.add('loading');
        }

        renderVideoPlayer(video).then(player => {
            if (player) {
                const isWistia = player.innerPlayer.uuid && player.innerPlayer.uuid.includes('wistia');

                if ("playerOnRender" in options) {
                    options.playerOnRender(player);
                }

                if (isWistia) {
                    player.play();

                    if (videoFrontElement) {
                        videoFrontElement.classList.add('playing');
                        videoFrontElement.classList.remove('loading');
                    }
                } else {
                    $(videoFrontElement).velocity({
                        opacity: 0
                    },
                    {
                        duration: 1000,
                        delay: 250,
                        begin: () => {
                            player.play();
                        },
                        complete: () => {
                            videoFrontElement.classList.add('playing');
                            videoFrontElement.classList.remove('loading');
                        }
                    });
                }
            }
        });
    }
};

const prepareVideos = (options =  {}) => {
    const videos = document.querySelectorAll('[data-video-player]');

    videos.forEach((video) => {
        const parameters = video.dataset.parameters;
        const provider = video.dataset.provider;

        if (parameters && parameters.autoplay === '1') {
            playVideoPlayer(video, options);
        }

        if (provider === 'wistia') {
            renderVideoPlayer(video)
            .then(player => {
                if (player) {
                    if ("playerOnRender" in options) {
                        options.playerOnRender(player);
                    }
                }
            });
        } else {
            video.addEventListener('click', () => {
                playVideoPlayer(video, options);
            });
        }
    });
};

export { playVideoPlayer, prepareVideos };